import React, { useState, useEffect } from 'react';
import { ArrowRight, Sparkles, Zap, Briefcase, BookOpen } from 'lucide-react';
import ResumeBuilderRouter from './ResumeBuilderRouter';
import ContestSubmissionForm from './ContestSubmissionForm';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const Hero = ({ data }) => {
  const [showResumeBuilder, setShowResumeBuilder] = useState(false);
  const [showContestForm, setShowContestForm] = useState(false);
  const { user, isVerified, showLogin } = useAuth();
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 640 : false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const defaultData = {
    headline: 'Win Big with Your Testing Expertise',
    subtitle: 'Show off your QA skills in our monthly contest! Share your best testing hacks, automation tricks, and innovative approaches.'
  };

  const safeData = data || defaultData;

  const renderHeadline = () => {
    const text = safeData.headline || defaultData.headline;
    if (text.includes('Testing')) {
      const parts = text.split('Testing');
      const prefix = parts[0];
      const hasWinBigWith = prefix.includes('Win Big with');
      
      return (
        <>
          {hasWinBigWith ? (
            <>
              Win Big with <br className="md:hidden" />Your{' '}
            </>
          ) : (
            prefix
          )}
          <span className="relative inline-block text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-400 to-indigo-400 filter drop-shadow-[0_0_12px_rgba(6,182,212,0.45)]">
            Testing
          </span>
          {parts[1]}
        </>
      );
    }
    return text;
  };

  const openResumeBuilder = () => {
    if (!user || !isVerified) {
      showLogin('resume', () => setShowResumeBuilder(true));
      return;
    }
    setShowResumeBuilder(true);
  };

  return (
    <section
      id="home"
      className="relative overflow-visible sm:overflow-hidden flex flex-col lg:flex-row items-start sm:items-center justify-start lg:justify-start pt-16 pb-4 sm:pt-8 sm:pb-4 lg:pt-28 lg:pb-12 bg-gradient-to-b from-[#0f172a] to-[#020617]"
    >
      {/* Subtle radial glow behind headline */}
      <div className="absolute top-1/3 left-1/4 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full opacity-25 blur-[100px] pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(99, 102, 241, 0.35) 0%, rgba(0, 0, 0, 0) 70%)' }} />

      {/* Dotted / grid pattern background */}
      <div className="absolute top-0 left-0 w-full h-full opacity-[0.03] pointer-events-none">
        <svg className="w-full h-full" fill="currentColor">
          <pattern id="dotted-pattern" x="0" y="0" width="24" height="24" patternUnits="userSpaceOnUse">
            <circle cx="3" cy="3" r="1.5" className="text-blue-300" />
          </pattern>
          <rect width="100%" height="100%" fill="url(#dotted-pattern)" />
        </svg>
      </div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 w-full z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 sm:gap-8 items-start sm:items-center">
          {/* Left Column (Content) */}
          <div className="lg:col-span-9 text-center lg:text-left">
            {/* Headline */}
            <h1 className="font-bold text-white tracking-[-0.04em] leading-[1.1] text-3xl sm:text-4xl lg:text-[40px] mb-6 relative z-10 max-w-2xl mx-auto lg:mx-0">
              {renderHeadline()}
            </h1>

            {/* Subtitle / Description */}
            <p className="text-xs sm:text-sm lg:text-base text-slate-300 max-w-xl mx-auto lg:mx-0 mb-8 leading-relaxed relative z-10">
              {safeData.subtitle || defaultData.subtitle}
            </p>

            {/* CTA Buttons */}
            {isMobile ? (
              /* Mobile view: 2x2 Grid for premium and space-saving design */
              <div
                className="w-full relative z-10"
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '9px'
                }}
              >
                {/* PRIMARY — AI Resume Builder */}
                <button
                  onClick={openResumeBuilder}
                  className="flex items-center justify-center gap-1.5 h-[42px] px-3 rounded-[12px] font-bold text-[11px] bg-gradient-to-r from-[#0057B7] to-[#0088FF] text-white shadow-[0_4px_12px_rgba(0,87,183,0.3)] active:scale-95 transition-all duration-200"
                >
                  <Sparkles className="w-3.5 h-3.5 text-[#FF6600] shrink-0 animate-pulse" />
                  <span>AI Resume Builder</span>
                </button>

                {/* SECONDARY — Live Events CTA */}
                <button
                  onClick={() => {
                    window.history.pushState({}, '', '/events');
                    window.dispatchEvent(new PopStateEvent('popstate'));
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="flex items-center justify-center gap-1.5 h-[42px] px-3 rounded-[12px] font-semibold text-[11px] bg-white/5 border border-white/10 text-slate-200 active:scale-95 transition-all duration-200"
                >
                  <Zap className="w-3.5 h-3.5 text-orange-400 shrink-0" />
                  <span>Live QA Events</span>
                </button>

                {/* TERTIARY — Find Jobs */}
                <button
                  onClick={() => toast('🚀 Coming Soon!', { duration: 2500, style: { background: '#0057B7', color: 'white' } })}
                  className="flex items-center justify-center gap-1.5 h-[42px] px-3 rounded-[12px] font-semibold text-[11px] bg-white/5 border border-white/10 text-slate-300 active:scale-95 transition-all duration-200"
                >
                  <Briefcase className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                  <span>Find Jobs</span>
                  <span className="text-[8px] bg-white/20 px-1 py-0.5 rounded-full leading-none shrink-0 font-bold ml-0.5">Soon</span>
                </button>

                {/* TERTIARY — Insight Library */}
                <button
                  onClick={() => {
                    if (!user || !isVerified) {
                      showLogin('insight-library', () => {
                        window.history.pushState({}, '', '/insight-library');
                        window.dispatchEvent(new PopStateEvent('popstate'));
                      });
                    } else {
                      window.history.pushState({}, '', '/insight-library');
                      window.dispatchEvent(new PopStateEvent('popstate'));
                    }
                  }}
                  className="flex items-center justify-center gap-1.5 h-[42px] px-3 rounded-[12px] font-semibold text-[11px] bg-white/5 border border-white/10 text-slate-300 active:scale-95 transition-all duration-200"
                >
                  <BookOpen className="w-3.5 h-3.5 text-purple-400 shrink-0" />
                  <span>Insight Library</span>
                </button>
              </div>
            ) : (
              /* Desktop / Tablet view: remains as a single row */
              <div className="flex flex-row gap-3 items-center justify-start relative z-10 w-full">
                {/* PRIMARY — AI Resume Builder */}
                <button
                  onClick={openResumeBuilder}
                  className="group h-11 px-5 rounded-[12px] font-bold text-xs sm:text-sm bg-gradient-to-r from-[#0057B7] to-[#0088FF] text-white shadow-[0_4px_15px_rgba(0,87,183,0.35)] hover:shadow-[0_4px_20px_rgba(0,87,183,0.55)] hover:scale-[1.02] hover:-translate-y-0.5 active:scale-98 transition-all duration-300 flex items-center justify-center gap-1.5 flex-shrink-0 w-full sm:w-auto"
                >
                  <Sparkles className="w-4 h-4 text-[#FF6600] flex-shrink-0" />
                  <span>AI Resume Builder</span>
                  <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform flex-shrink-0 hidden sm:block" />
                </button>

                {/* SECONDARY — Live Events CTA */}
                <button
                  onClick={() => {
                    window.history.pushState({}, '', '/events');
                    window.dispatchEvent(new PopStateEvent('popstate'));
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="group relative h-11 px-4 lg:px-5 rounded-[12px] font-semibold text-xs sm:text-sm bg-white/5 border border-white/5 hover:bg-gradient-to-r hover:from-[#0057B7] hover:to-[#0088FF] hover:border-transparent backdrop-blur-md text-slate-200 hover:text-white hover:scale-[1.02] hover:-translate-y-0.5 hover:shadow-[0_4px_20px_rgba(0,87,183,0.55)] active:scale-98 transition-all duration-200 flex items-center justify-center gap-1.5 overflow-hidden flex-shrink-0 w-full sm:w-auto"
                >
                  <Zap className="w-4 h-4 text-orange-400 group-hover:text-white flex-shrink-0 animate-pulse" />
                  <span className="relative z-10">🔥 Explore Live QA Events</span>
                  <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform flex-shrink-0 hidden sm:block" />
                </button>

                {/* TERTIARY — Find Jobs */}
                <button
                  onClick={() => toast('🚀 Coming Soon!', { duration: 2500, style: { background: '#0057B7', color: 'white' } })}
                  className="h-11 px-3.5 rounded-[12px] font-medium text-xs sm:text-sm bg-white/5 border border-white/5 hover:bg-gradient-to-r hover:from-[#0057B7] hover:to-[#0088FF] hover:border-transparent backdrop-blur-md text-slate-300 hover:text-white hover:scale-[1.02] hover:-translate-y-0.5 hover:shadow-[0_4px_20px_rgba(0,87,183,0.55)] active:scale-98 transition-all duration-300 flex items-center justify-center gap-1.5 flex-shrink-0 w-full sm:w-auto"
                >
                  <Briefcase className="w-4 h-4 group-hover:text-white flex-shrink-0" />
                  <span>Find Jobs</span>
                  <span className="text-[10px] bg-white/20 group-hover:bg-white/30 px-1.5 py-0.5 rounded-full leading-none flex-shrink-0">Soon</span>
                </button>

                {/* TERTIARY — Insight Library */}
                <button
                  onClick={() => {
                    if (!user || !isVerified) {
                      showLogin('insight-library', () => {
                        window.history.pushState({}, '', '/insight-library');
                        window.dispatchEvent(new PopStateEvent('popstate'));
                      });
                    } else {
                      window.history.pushState({}, '', '/insight-library');
                      window.dispatchEvent(new PopStateEvent('popstate'));
                    }
                  }}
                  className="h-11 px-3.5 rounded-[12px] font-medium text-xs sm:text-sm bg-white/5 border border-white/5 hover:bg-gradient-to-r hover:from-[#0057B7] hover:to-[#0088FF] hover:border-transparent backdrop-blur-md text-slate-300 hover:text-white hover:scale-[1.02] hover:-translate-y-0.5 hover:shadow-[0_4px_20px_rgba(0,87,183,0.55)] active:scale-98 transition-all duration-300 flex items-center justify-center gap-1.5 flex-shrink-0 w-full sm:w-auto"
                >
                  <BookOpen className="w-4 h-4 group-hover:text-white flex-shrink-0" />
                  <span>Insight Library</span>
                </button>
              </div>
            )}
          </div>

          {/* Right Column (Subtle Glowing AI Orb) */}
          <div className="lg:col-span-3 hidden sm:flex justify-center items-center relative mt-8 lg:mt-0">
            {/* Subtle radial glow on right side */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full opacity-40 blur-[40px] pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(0, 136, 255, 0.25) 0%, rgba(10, 47, 130, 0) 70%)' }} />

            <div className="relative w-48 h-48 flex items-center justify-center">
              {/* Spinning SVG orbital rings */}
              <svg className="w-full h-full animate-[spin_20s_linear_infinite] opacity-60" viewBox="0 0 100 100">
                <circle cx="50" cy="50" r="42" stroke="url(#orb-grad-1)" strokeWidth="1" fill="none" strokeDasharray="30 40" />
                <circle cx="50" cy="50" r="34" stroke="url(#orb-grad-2)" strokeWidth="0.75" fill="none" strokeDasharray="15 30" />
                <defs>
                  <linearGradient id="orb-grad-1" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#00E5FF" stopOpacity="0.8" />
                    <stop offset="100%" stopColor="#6366F1" stopOpacity="0.1" />
                  </linearGradient>
                  <linearGradient id="orb-grad-2" x1="1" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#ec4899" stopOpacity="0.6" />
                    <stop offset="100%" stopColor="#00E5FF" stopOpacity="0.1" />
                  </linearGradient>
                </defs>
              </svg>

              {/* Inner glowing pulse core */}
              <div className="absolute w-16 h-16 rounded-full bg-gradient-to-tr from-cyan-500/20 to-indigo-500/20 border border-cyan-500/30 backdrop-blur-md shadow-[0_0_25px_rgba(6,182,212,0.3)] flex items-center justify-center">
                <div className="w-9 h-9 rounded-full bg-slate-950/80 border border-indigo-500/30 flex items-center justify-center">
                  <div className="w-2.5 h-2.5 rounded-full bg-cyan-400 animate-ping" />
                  <div className="absolute w-2 h-2 rounded-full bg-cyan-400 shadow-[0_0_8px_#22d3ee]" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ResumeBuilderRouter
        isOpen={showResumeBuilder}
        onClose={() => setShowResumeBuilder(false)}
      />

      <ContestSubmissionForm
        isOpen={showContestForm}
        onClose={() => setShowContestForm(false)}
        contestData={{
          theme: 'Advanced Testing Methodologies',
          deadline: (() => {
            const now = new Date();
            const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
            const y = lastDay.getFullYear();
            const m = String(lastDay.getMonth() + 1).padStart(2, '0');
            const d = String(lastDay.getDate()).padStart(2, '0');
            return `${y}-${m}-${d}`;
          })()
        }}
      />
    </section>
  );
};

export default Hero;
